// --- src/data/m3/Unit7.js ---

export const M3_UNIT_7 = {
  id: "m3-u7-frequency-routines",
  title: "Unit 7: Frequency & Routines",
  topic: "Frequency adverbs, How often vs When, Daily routines",
  iconType: "clock",
  grammarImage: "/m3-u7-frequency.jpg",
  grammarText: "Master frequency adverbs (always, usually, never), understand the difference between 'how often' and 'when', and describe daily routines with connectors (because, so, and, but).",
  sets: [

    // ===================================================================
    // SET 1: FREQUENCY ADVERBS SCALE (A2)
    // ===================================================================
    {
      id: "m3-u7-set1",
      title: "Set 1: Frequency Scale 📊",
      context: `*Lunes por la mañana - Oficina en Ottawa*

David es oficinista en Ottawa. Hoy habla sobre sus rutinas diarias.
"Siempre me levanto a las 7 AM, usualmente desayuno, y a veces me ejercito."
Sus compañeros aprenden sobre frecuencias.

*Monday morning - Office in Ottawa*

David is an office worker in Ottawa. Today he talks about his daily routines.
"I always wake up at 7 AM. I usually have breakfast. I sometimes exercise."
His coworkers learn about frequencies.`,
      
      questions: [
        {
          type: "match",
          q: "Match the frequency adverb to its percentage.",
          pairs: [
            { left: "always 100%", right: "siempre" },
            { left: "never 0%", right: "nunca" },
            { left: "sometimes 50%", right: "a veces" }
          ],
          a: "completed",
        },
        {
          type: "mc",
          q: "David: 'I ___ wake up at 7 AM.' (100%)",
          options: ["always", "sometimes", "never"],
          a: "always",
          tip: "Always = 100% = siempre!"
        },
        {
          type: "input",
          q: "Complete: 'She usually ___ breakfast at 8am.'",
          a: "eats",
          alts: ["has", "takes"],
          tip: "Usually = ~80% = usualmente!",
          successTip: "Para comidas normales usa → eat o have. Para medicinas, suplementos o decisiones → take. 🔴"
        },
        {
          type: "match",
          q: "Match more frequencies.",
          pairs: [
            { left: "usually 80%", right: "usualmente" },
            { left: "often 60%", right: "a menudo" },
            { left: "rarely 20%", right: "rara vez" }
          ],
          a: "completed",
          successTip: "Complete scale! 100% → 0%! 📈"
        },
        {
          type: "order",
          q: "Order from MOST (100%) to LEAST (0%).",
          words: ["always,", "usually,", "often,", "sometimes,", "rarely,", "never"],
          a: "always, usually, often, sometimes, rarely, never",
          extra: ["frequently", "seldom"],
          tip: "This is THE scale!"
        },
        {
          type: "mc",
          q: "David: 'I ___ go to the gym. I know is not everyday but it's something.'",
          options: ["often", "always", "never"],
          a: "often",
          tip: "Often = ~60% = a menudo!",
          successTip: "Often! Good frequency! 🟠"
        },
        {
          type: "input",
          q: "Complete: 'They ___ eat fast food because they are healthy.'",
          a: "rarely",
          alts: ["never", "sometimes"],
          tip: "Rarely = ~20% = rara vez!",
          successTip: "Rarely! Low frequency! 🟡"
        },
        {
          type: "order",
          q: "Traduce: 'A veces juego videojuegos.'",
          words: ["I", "sometimes", "play", "video", "games."],
          a: "I sometimes play video games.",
          extra: ["always", "never", "am"],
          tip: "Sometimes BEFORE main verb!"
        },
        {
          type: "mc",
          q: "Complete: 'He hates coffee, so he ___ drinks it'",
          options: ["never", "always", "usually"],
          a: "never",
          tip: "Never = 0% = nunca!",
        },
        {
          type: "input",
          q: "Complete: 'We ___ watch TV in the evening, but only when we have free time'",
          a: "sometimes",
          alts: ["usually", "often", "seldom", "rarely"],
          tip: "Any frequency adverb works!",
          successTip: "'Puedes decir 'free time' o 'spare time' 🎯"
        }
      ]
    },
    // ===================================================================
    // SET 2: DAILY ROUTINES IN CONTEXT (A2)
    // ===================================================================
    {
      id: "m3-u7-set2",
      title: "Set 2: Daily Routines 🌅",
      context: `*Martes - Comparando rutinas*

David compara su rutina con la de sus compañeros.
"Me levanto, me cepillo los dientes y desayuno. ¿Y tú?"
Cada persona tiene rutinas diferentes.

*Tuesday - Comparing routines*

David compares his routine with his coworkers.
"I wake up, brush my teeth, and have breakfast. And you?"
Each person has different routines.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match the routine activity.",
          pairs: [
            { left: "wake up 🌅", right: "despertarse" },
            { left: "brush teeth 🦷", right: "cepillarse los dientes" },
            { left: "have breakfast 🍳", right: "desayunar" }
          ],
          a: "completed",
          tip: "Morning routines!",
          successTip: "Morning vocabulary! Essential! ☀️"
        },
        {
          type: "mc",
          q: "Tom starts work at 8 AM, so he wakes up at:",
          options: ["7 AM", "9 AM", "11 AM"],
          a: "7 AM",
          tip: "Work at 8 = wake before!",
          successTip: "7 AM! Early work = early wake! ⏰"
        },
        {
          type: "mc",
          q: "Sarah cares about dental health. She brushes her teeth:",
          options: ["three times a day", "once a week", "never"],
          a: "three times a day",
          tip: "Cares = good habits!",
          successTip: "Thrice existe pero mejor di three times 🦷 😊"
        },
        {
          type: "match",
          q: "Match more routines.",
          pairs: [
            { left: "get dressed 👔", right: "vestirse" },
            { left: "go to work 💼", right: "ir al trabajo" },
            { left: "come home 🏠", right: "llegar a casa" }
          ],
          a: "completed",
          tip: "Daily activities!",
          successTip: "Daily routine vocabulary! 🎯"
        },
        {
          type: "mc",
          q: "Mike's office is downtown. He gets there by:",
          options: ["going to work", "coming home", "waking up"],
          a: "going to work",
          tip: "Downtown = work location!",
          successTip: "US = Downtown, UK = City Centre! 💼"
        },
        {
          type: "input",
          q: "David starts work at 8:30, so he always ___ ___ at 7 AM.",
          a: "wakes up",
          alts: ["gets up"],
          tip: "Early work = early wake!",
          successTip: "Wakes up! Morning routine! 🌅"
        },
        {
          type: "order",
          q: "Traduce: 'Me visto después del desayuno.'",
          words: ["I", "get", "dressed", "after", "breakfast."],
          a: "I get dressed after breakfast.",
          extra: ["dress", "dressing", "am"],
          tip: "Get dressed = vestirse!"
        },
        {
          type: "mc",
          q: "She finishes work at 6 PM, so she ___ at 6:30.",
          options: ["comes home", "goes to work", "wakes up"],
          a: "comes home",
          tip: "Finish work = return home!",
          successTip: "Comes home! End of workday! 🏠"
        },
        {
          type: "input",
          q: "They work early mornings, so they usually ___ to bed at 10 PM.",
          a: "go",
          alts: [],
          tip: "Early work = early sleep!",
          successTip: "Go to bed! Early schedule! 😴"
        },
        {
          type: "order",
          q: "Traduce: 'Ceno a las 7 PM todos los días.'",
          words: ["I", "have", "dinner", "at", "7", "PM", "every", "day."],
          a: "I have dinner at 7 PM every day.",
          extra: ["come", "make", "do"],
          tip: "Have dinner = cenar!"
        }
      ]
    },

    // ===================================================================
    // SET 3: COMPARING FREQUENCIES (A2)
    // ===================================================================
    {
      id: "m3-u7-set3",
      title: "Set 3: Comparing Habits 📊",
      context: `*Miércoles - Comparando hábitos*

David y sus compañeros comparan sus hábitos diarios.
"Yo siempre desayuno, pero Tom a veces se lo salta."
Comparan frecuencias de diferentes actividades.

*Wednesday - Comparing habits*

David and his coworkers compare their daily habits.
"I always have breakfast, but Tom sometimes skips it."
They compare frequencies of different activities.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "Tom exercises 6 days a week. Sarah exercises once a month. Who exercises more often?",
          options: ["Tom", "Sarah", "The same for both"],
          a: "Tom",
          tip: "6 days > 1 day!",
          successTip: "Tom! More days = more often! 💪"
        },
        {
          type: "mc",
          q: "David always arrives at 8 AM. Mike sometimes arrives at 8 AM. Who is more punctual?",
          options: ["David", "Mike", "The same for both"],
          a: "David",
          tip: "Always > sometimes!",
          successTip: "David! Always = 100% punctual! ⏰"
        },
        {
          type: "match",
          q: "Match the person to their frequency.",
          pairs: [
            { left: "Sarah eats breakfast every day", right: "always" },
            { left: "Tom skips breakfast 3 times aweek", right: "sometimes" },
            { left: "Mike doesn't eat breakfast", right: "never" }
          ],
          a: "completed",

        },
        {
          type: "mc",
          q: "Lisa works from home five days a week, but Tom commutes every day. Who commutes more?",
          options: ["Tom", "Lisa", "The same"],
          a: "Tom",
          tip: "Daily > 2 days!",
          successTip: "Commute = Travel from home to work! 🚌"
        },
        {
          type: "input",
          q: "David drinks coffee every morning. His habit frequency is:",
          a: "always",
          alts: ["every day"],
          tip: "Every morning = 100%!",
          successTip: "Always! Daily coffee routine! ☕"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "mc",
          q: "Sarah goes to the gym Monday to Saturday. She ___ exercises.",
          options: ["usually", "rarely", "never"],
          a: "usually",
          tip: "6 out of 7 days!",
          successTip: "Usually! Very regular! 🏋️"
        },
        {
          type: "input",
          q: "Tom is a vegetarian, so he ___ eats meat, but Sarah eats it daily.",
          a: "never",
          alts: [],
          tip: "Vegetarian = 0% meat!",
          successTip: "Never! Vegetarian diet! 🥗"
        },
        {
          type: "order",
          q: "Traduce: 'Siempre llego temprano, pero él a veces llega tarde.'",
          words: ["I", "always", "arrive", "early,", "but", "he", "sometimes", "arrives", "late."],
          a: "I always arrive early, but he sometimes arrives late.",
          extra: ["am", "is", "never"],
          tip: "Contrast with 'but'!"
        },
        {
          type: "mc",
          q: "David cooks dinner 5 nights a week, so he ___ cooks at home.",
          options: ["usually", "rarely", "never"],
          a: "usually",
          tip: "5 out of 7 = high frequency!",
          successTip: "Usually! Most nights! 🍽️"
        },
        {
          type: "input",
          q: "She works night shifts, so she ___ wakes up early, but I always do.",
          a: "rarely",
          alts: ["never"],
          successTip: "Night shift = Turno de noche!",
        }
      ]
    },

    // ===================================================================
    // SET 4: FREQUENCY + ROUTINES INTEGRATED (A2+)
    // ===================================================================
    {
      id: "m3-u7-set4",
      title: "Set 4: Describing Complete Habits 🔄",
      context: `*Jueves - Describiendo hábitos completos*

David describe sus hábitos con frecuencias específicas.
"Siempre me despierto temprano porque trabajo a las 8. Usualmente desayuno en casa."
Combina rutinas con frecuencias.

*Thursday - Describing complete habits*

David describes his habits with specific frequencies.
"I always wake up early because I work at 8. I usually have breakfast at home."
He combines routines with frequencies.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "David says: 'I always wake up at 7 because I start work at 8.' Why does he wake up early?",
          options: ["Because he starts work at 8", "Because he likes mornings", "Because he exercises"],
          a: "Because he starts work at 8",

        },
        {
          type: "mc",
          q: "'Sarah usually has breakfast at home because it's cheaper.' Why does she eat at home?",
          options: ["It's cheaper", "It's healthier", "It's faster"],
          a: "It's cheaper",
        },
        {
          type: "match",
          q: "Match the complete habit.",
          pairs: [
            { left: "I always wake up early", right: "100% morning person" },
            { left: "I sometimes exercise", right: "50% gym visits" },
            { left: "I never skip breakfast", right: "0% missed meals" }
          ],
          a: "completed",
          successTip: "Skip significa no hacer algo intencionalmente, por ejemplo no ir a clase o no comer una comida."
        },
        {
          type: "mc",
          q: "'Tom rarely eats fast food because he's on a diet.' How often does he eat fast food?",
          options: ["Not very often", "Every day", "Always"],
          a: "Not very often",
          tip: "Rarely = low frequency!",
          successTip: "Rarely = Seldom, but rarely is more natural!"
        },
        {
          type: "input",
          q: "'Mike goes to bed at 10 PM because he wakes up at 6 AM.' He sleeps:",
          a: "early",
          alts: ["at 10", "at 10 PM"],
          tip: "10 PM = early bedtime!",
          successTip: "Early! Early wake = early sleep! 😴"
        },
        {
          type: "order",
          q: "Traduce: 'Siempre me despierto a las 7 porque trabajo a las 8.'",
          words: ["I", "always", "wake", "up", "at", "7", "because", "I", "work", "at", "8."],
          a: "I always wake up at 7 because I work at 8.",
          extra: ["so", "but", "am"],
          tip: "Because = reason!"
        },
        {
          type: "mc",
          q: "She lives close to her office, so she ___ commutes by car.",
          options: ["rarely", "always", "usually"],
          a: "rarely",
          tip: "Close = walks!",
          successTip: "Close to y near significan “cerca”, pero close to enfatiza más proximidad y es más común en conversación"
        },
        {
          type: "input",
          q: "He's a morning person, so he ___ wakes up before 7 AM.",
          a: "always",
          alts: ["usually"],
          tip: "Morning person = early!",
          successTip: "Always! Loves mornings! 🌅"
        },
        {
          type: "order",
          q: "Traduce: 'Usualmente cocino en casa porque es más saludable.'",
          words: ["I", "usually", "cook", "at", "home", "because", "it's", "healthier."],
          a: "I usually cook at home because it's healthier.",
          extra: ["so", "but", "always"],
          tip: "Because = reason!"
        },
        {
          type: "input",
          q: "They work night shifts, so they ___ have breakfast in the morning, but they have it at noon.",
          a: "never",
          alts: ["rarely"],
          tip: "Night shift = different schedule!",
          successTip: "Never! Different meal times! 🌙"
        }
      ]
    },

    // ===================================================================
    // SET 5: EXPRESSIONS OF FREQUENCY (A2+)
    // ===================================================================
    {
      id: "m3-u7-set5",
      title: "Set 5: Specific Frequencies ⏰",
      context: `*Viernes - Frecuencias específicas*

David usa números específicos para describir frecuencias.
"Voy al gimnasio tres veces por semana. Como fuera dos veces al mes."
Las expresiones numéricas son más precisas.

*Friday - Specific frequencies*

David uses specific numbers to describe frequencies.
"I go to the gym three times a week. I eat out twice a month."
Numeric expressions are more precise.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match the frequency expression.",
          pairs: [
            { left: "once a week", right: "una vez por semana" },
            { left: "twice a month", right: "dos veces al mes" },
            { left: "every day", right: "todos los días" }
          ],
          a: "completed",
        },
        {
          type: "mc",
          q: "Tom exercises Monday, Wednesday, and Friday. He exercises:",
          options: ["three times a week", "once a week", "every day"],
          a: "three times a week",
          tip: "3 days = 3 times!",
          successTip: "He works out three times a week! 🏋️"
        },
        {
          type: "mc",
          q: "Sarah visits her family on the 1st and 15th of each month. She visits:",
          options: ["twice a month", "once a month", "every day"],
          a: "twice a month",
          tip: "2 times = twice!",
          successTip: "Twice a month! Two visits! 👨‍👩‍👧"
        },
        {
          type: "match",
          q: "Match number to expression.",
          pairs: [
            { left: "1 time", right: "once" },
            { left: "2 times", right: "twice" },
            { left: "3 times", right: "three times" }
          ],
          a: "completed",
          tip: "Once, twice, then + times!",
          successTip: "Pattern! Special words for 1 & 2! 🔢"
        },
        {
          type: "mc",
          q: "David brushes his teeth in the morning and at night. He brushes:",
          options: ["twice a day", "once a day", "three times a day"],
          a: "twice a day",
          tip: "Morning + night = 2!",
          successTip: "Twice a day! Good dental health! 🦷"
        },
        {
          type: "input",
          q: "He loves fitness, so he goes to the gym ___ a week.",
          a: "three times",
          alts: ["3 times", "four times", "4 times", "five times", "5 times", "6 times", "six times", "7 times", "seven times", "8 times", "eight times", "9 times", "nine times", "10 times", "ten times",],
          tip: "Loves fitness = many times!",
          successTip: "Multiple times! Regular gym visits! 🏋️"
        },
        {
          type: "order",
          q: "Traduce: 'Como fuera dos veces por semana.'",
          words: ["I", "eat", "out", "twice", "a", "week."],
          a: "I eat out twice a week.",
          extra: ["time", "two", "times"],
          tip: "Twice = dos veces!",
          successTip: "Eat out is a useful expression! 🍔"
        },
        {
          type: "mc",
          q: "She's a dedicated student, so she studies English ___.",
          options: ["every day", "once a month", "never"],
          a: "every day",
          tip: "Dedicated = daily!",
          successTip: "Every day! Committed student! 📚"
        },
        {
          type: "input",
          q: "They live far from their family, so they only visit them ___ a month.",
          a: "once",
          alts: ["twice"],
          tip: "Far = less frequent!",
        },
        {
          type: "order",
          q: "Traduce: 'Veo películas tres veces al mes.'",
          words: ["I", "watch", "movies", "three", "times", "a", "month."],
          a: "I watch movies three times a month.",
          extra: ["twice", "time", "every"],
          tip: "Three times!",
          successTip: "Each month → cada mes, Every month → todos los meses, A month → al mes."
        }
      ]
    },
 {
      id: "m3-u7-set6",
      title: "Set 6: Dialogue - Morning Habits 💬",
      context: `*Sábado - Conversación en la cafetería*

David conversa con su compañera Sarah sobre rutinas matutinas.
"Entonces, ¿a qué hora te levantas usualmente?" pregunta David.

*Saturday - Café conversation*

David talks with his coworker Sarah about morning routines.
"So, what time do you usually wake up?" asks David.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Conversation Start**

David: "Hi Sarah! What time do you usually wake up?"
Sarah: "..."
David: "Oh, that's pretty early! Do you have breakfast afterwards?"
Sarah: "..."`
        },
        {
          type: "input",
          q: "Complete Sarah's first answer: 'I usually ___ ___ at seven AM.'",
          a: "wake up",
          alts: ["get up","do it"],
          successTip: "Do it = wake up! Recuerda que puedes contestar usando auxiliaes ⏰"
        },
        {
          type: "mc",
          q: "Complete Sarah's response: 'Yes, I ___ have breakfast.'",
          options: ["always", "never", "rarely"],
          a: "always",
          tip: "She says YES, so...",
          successTip: "✓ Afterwards = al final, describe lo que pasa después. 🍳"
        },
        {
          type: "passage",
          passage: `**Dialogue Continues**

David: "What do you usually have for breakfast?"
Sarah: "I usually have coffee and toast. What about you?"
David: "..."
Sarah: "Really? That's a lot!"`
        },
        {
          type: "order",
          q: "Complete David's answer: Yo siempre desayuno huevos, pan y jugo.",
          words: ["I", "always", "have", "eggs,", "toast,", "and", "juice."],
          a: "I always have eggs, toast, and juice.",
          extra: ["am", "breakfast", "sometimes"],
          tip: "Have = eat breakfast, lunc or dinner!"
        },
        {
          type: "passage",
          passage: `**More Questions**

Sarah: "Do you exercise in the morning?"
David: "..."
Sarah: "That's good! How often do you go to the gym?"
David: "..."`
        },
        {
          type: "mc",
          q: "Complete David's answer about exercise: 'Yes, I ___ exercise in the morning.'",
          options: ["usually", "never", "rarely"],
          a: "usually",
          tip: "He answers YES!",
        },
        {
          type: "input",
          q: "Complete David's answer about frequency: 'I go to the gym ___ times a week.'",
          a: "three",
          alts: ["3", "four", "4", "five", "5", "two", "2", "six", "6", "seven", "7", "eight", "8", "nine", "9", "ten", "10", "once", "twice", "thrice"],
          tip: "Any reasonable number!",
          successTip: "✓ SPECIFIC FREQUENCY!\nNumber + times pattern! 🏋️"
        },
        {
          type: "passage",
          passage: `**Wrapping Up**

Sarah: "Do you ever skip your morning routine?"
David: "..."
Sarah: "Wow, you're very disciplined!"
David: "Well, I try!"`
        },
        {
          type: "mc",
          q: "Complete David's response: 'I ___ skip my routine.'",
          options: ["rarely", "always", "usually"],
          a: "rarely",
          tip: "He's disciplined!",
          successTip: "✓ RARELY!\nLow frequency = disciplined! ⭐"
        },
        {
          type: "order",
          q: "Complete the closing: Yo debería ser más disciplinada.",
          words: ["I", "should", "be", "more", "disciplined!"],
          a: "I should be more disciplined!",
          extra: ["am", "never", "always"],
          tip: "Should = debería!",
          successTip: "✓ Should be = debería ser 💬"
        }
      ]
    },

    // ===================================================================
    // SET 7: INTERACTIVE DIALOGUE - Weekend Activities 🎯
    // ===================================================================
    {
      id: "m3-u7-set7",
      title: "Set 7: Dialogue - Weekend Plans 🎯",
      context: `*Domingo - Conversación sobre el fin de semana*

David habla con su amigo Mike sobre actividades de fin de semana.
"¿Qué haces usualmente los fines de semana?" pregunta David.
Completa las respuestas de Mike en la conversación.

*Sunday - Weekend conversation*

David talks with his friend Mike about weekend activities.
"What do you usually do on weekends?" asks David.
Complete Mike's answers in the conversation.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Weekend Talk**

David: "Hey Mike! What do you usually do on weekends?"
Mike: "..."
David: "Nice! How often do you go?"
Mike: "..."`
        },
        {
          type: "mc",
          q: "Complete Mike's answer: 'I usually ___ to the movies.'",
          options: ["go", "goes", "going"],
          a: "go",
          tip: "I + base form!",
          successTip: "✓ GO!\nPresent simple! 🎬"
        },
        {
          type: "input",
          q: "Complete Mike's frequency answer: 'I go ___ a month.'",
          a: "twice",
          alts: ["two times", "once", "three times","four times"],
        },
        {
          type: "passage",
          passage: `**More Details**

David: "Do you always go alone?"
Mike: "..."
David: "Oh, that's fun! When do you usually go?"
Mike: "..."`
        },
        {
          type: "mc",
          q: "Complete Mike's answer: 'No, I ___ go with friends.'",
          options: ["usually", "never", "rarely"],
          a: "usually",
          tip: "He says NO (not alone)!",
          successTip: "✓ USUALLY!\nWith friends = social! 👥"
        },
        {
          type: "order",
          q: "Complete Mike's answer about timing.",
          words: ["I", "usually", "go", "on", "Saturday", "evenings."],
          a: "I usually go on Saturday evenings.",
          extra: ["at", "in", "goes"],
          tip: "ON + day + time!"
        },
        {
          type: "passage",
          passage: `**David's Turn**

Mike: "What about you? What do you do on weekends?"
David: "..."
Mike: "Cool! How often?"
David: "..."`
        },
        {
          type: "input",
          q: "Complete David's answer: 'I usually ___ sports.'",
          a: "play",
          alts: ["watch", "do"],
          tip: "Weekend activity!",
        },
        {
          type: "mc",
          q: "Complete David's frequency: 'I play soccer ___ a week. On Friday and Sunday'",
          options: ["twice", "two", "second","two times"],
          a: "twice",
          tip: "Twice = 2 times!",
          successTip: "✓ TWICE A WEEK!\nRegular activity! ⚽"
        },
        {
          type: "passage",
          passage: `**Final Exchange**

Mike: "Do you ever stay home and relax?"
David: "..."
Mike: "That's good. Everyone needs rest!"
David: "Exactly!"`
        },
        {
          type: "order",
          q: "Complete David's honest answer.",
          words: ["Yes,", "I", "sometimes", "stay", "home", "and", "relax."],
          a: "Yes, I sometimes stay home and relax.",
          extra: ["am", "never", "always"],
          tip: "Sometimes = balance!",
          successTip: "✓ BALANCE!\nSometimes active, sometimes rest! Perfect dialogue! 😌"
        }
      ]
    },
 {
      id: "m3-u7-set8",
      title: "Set 8: How Often vs When ❓",
      context: `*Lunes siguiente - Aprendiendo diferencias*

David enseña la diferencia entre "How often" y "When".
"'How often' pregunta por FRECUENCIA. 'When' pregunta por TIEMPO ESPECÍFICO."
Esta distinción es muy importante.

*Next Monday - Learning differences*

David teaches the difference between "How often" and "When".
"'How often' asks for FREQUENCY. 'When' asks for SPECIFIC TIME."
This distinction is very important.`,
      
      questions: [
        {
          type: "match",
          q: "Match question to what it asks.",
          pairs: [
            { left: "How often?", right: "Frequency (twice a week)" },
            { left: "When?", right: "Time (on Mondays)" },
            { left: "What time?", right: "Exact hour (at 7 PM)" }
          ],
          a: "completed",
          successTip: "How often ≠ When ≠ What time! 🎯"
        },
        {
          type: "mc",
          q: "Question: 'How often do you exercise?' Best answer:",
          options: ["Three times a week", "In the morning", "At 7 AM"],
          a: "Three times a week",
          tip: "How often = frequency!",
        },
        {
          type: "mc",
          q: "Question: 'When do you study?' Best answer:",
          options: ["In the evenings", "Three times a week", "For two hours"],
          a: "In the evenings",
          tip: "When = time of day!",
          successTip: "Time answer! Specific period! 🌙"
        },
        {
          type: "match",
          q: "Match question to correct answer type.",
          pairs: [
            { left: "How often?", right: "Twice a month" },
            { left: "When?", right: "On Saturday mornings" },
            { left: "What time?", right: "At 6:30 AM" }
          ],
          a: "completed",
          tip: "Each has specific answer!",
          successTip: "Question-answer matching! ✅"
        },
        {
          type: "mc",
          q: "Which is WRONG for 'How often do you cook?'",
          options: ["At 7 PM", "Every day", "Twice a week"],
          a: "At 7 PM",
          tip: "At 7 PM = What time!",
          successTip: "At 7 PM is time, not frequency! ⚠️"
        },
        {
          type: "order",
          q: "Traduce: '¿Con qué frecuencia estudias?'",
          words: ["How", "often", "do", "you", "study?"],
          a: "How often do you study?",
          extra: ["When", "What", "time"],
          tip: "How often = frequency question!"
        },
        {
          type: "input",
          q: "Answer 'How often do you go to the gym?' → I ___ three times a week.",
          a: "go",
          alts: ["do it"],
        },
        {
          type: "order",
          q: "Traduce: '¿Cuándo haces ejercicio?'",
          words: ["When", "do", "you", "exercise?"],
          a: "When do you exercise?",
          extra: ["How", "often", "What"],
          tip: "When = time question!",
          successTip: "When do you DO exercise suena muy repetitiva"
        },
        {
          type: "input",
          q: "Answer 'When do you go to the gym?' → I go ___ Mondays.",
          a: "on",
          successTip: "Time answer! Specific day! 📅"
        },
        {
          type: "mc",
          q: "Complete: '___ ___ do you play tennis?'→ Twice a week",
          options: ["How often", "What time", "When exactly"],
          a: "How often",
          tip: "Asking for frequency!",
          successTip: "How often! Frequency question! 🎾"
        }
      ]
    },
    {
      id: "m3-u7-set9",
      title: "Set 9: Because, So, But, And 🔗",
      context: `*Martes - Conectando ideas*

David enseña a conectar ideas de forma natural.
"Me levanto temprano PORQUE empiezo a trabajar a las 8. Estoy cansado, ENTONCES tomo café."
Los conectores hacen las frases más completas.

*Tuesday - Connecting ideas*

David teaches to connect ideas naturally.
"I wake up early BECAUSE I start work at 8. I'm tired, SO I drink coffee."
Connectors make sentences more complete.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "'I wake up early because I start work at 8.' Why does he wake up early?:",
          options: ["He starts work at 8", "He likes mornings", "He exercises"],
          a: "He starts work at 8",
          tip: "Because shows reason!",
          successTip: "Because = reason! Work schedule! ⏰"
        },
        {
          type: "mc",
          q: "'I'm tired, so I drink coffee.' What does he do when he is tired?",
          options: ["Drink coffee", "Go to bed", "Exercise"],
          a: "Drinks coffee",
          tip: "So shows result!",
          successTip: "So = result! Coffee for energy! ☕"
        },
        {
          type: "match",
          q: "Match connector to its function.",
          pairs: [
            { left: "because", right: "gives reason (porque)" },
            { left: "so", right: "shows result (entonces)" },
            { left: "but", right: "shows contrast (pero)" }
          ],
          a: "completed",
          tip: "Three main connectors!",
          successTip: "Connectors for logic! 🔗"
        },
        {
          type: "mc",
          q: "'I like coffee, but I don't like tea.' This shows:",
          options: ["Contrast", "Reason", "Result"],
          a: "Contrast",
          tip: "But shows difference!",
          successTip: "But = contrast! Likes vs dislikes! ☕🆚🍵"
        },
        {
          type: "mc",
          q: "'She exercises and has breakfast every day.' This connects:",
          options: ["Two actions", "Reason and result", "Contrast"],
          a: "Two actions",
          tip: "And links activities!",
          successTip: "And = addition! Two habits! 🔗"
        },
        {
          type: "mc",
          q: "Complete: 'I wake up early ___ I start work at 8.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "Showing reason!",
          successTip: "Because! Work is the reason! 🎯"
        },
        {
          type: "input",
          q: "Complete: 'I'm tired, ___ I go to bed early.'",
          a: "so",
          alts: [],
          tip: "Showing result!",
          successTip: "So! Tired → sleep = result! 😴"
        },
        {
          type: "order",
          q: "Traduce: 'Corro porque es saludable.'",
          words: ["I", "run", "because", "it's", "healthy."],
          a: "I run because it's healthy.",
          extra: ["so", "but", "and"],
          tip: "Because = reason!"
        },
        {
          type: "mc",
          q: "Complete: 'I want to exercise, ___ I don't have time.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "Showing contrast!",
          successTip: "But! Want vs reality! ⏰"
        },
        {
          type: "order",
          q: "Traduce: 'Estoy ocupado, entonces no puedo salir hoy.'",
          words: ["I'm", "busy,", "so", "I", "can't", "go", "out", "today."],
          a: "I'm busy, so I can't go out today.",
          extra: ["because", "but", "and"],
          tip: "So = result!",
          successTip: "So! Busy → can't go = result! Perfect! 📅"
        }
      ]
    },
 {
      id: "m3-u7-set10",
      title: "Set 10: Complete Routines 🏆",
      context: `*Miércoles - Evaluación final*

David evalúa la capacidad de describir rutinas completas.
"¡Combinen todo: frecuencia, tiempo, conectores y rutinas!"
Es hora de demostrar todo lo aprendido.

*Wednesday - Final evaluation*

David evaluates the ability to describe complete routines.
"Combine everything: frequency, time, connectors, and routines!"
Time to demonstrate everything learned.`,
      
      questions: [
         {
          type: "mc",
          q: "'I always wake up at 7 because I work at 8, so I have breakfast quickly.' How many ideas are connected?",
          options: ["Three", "Two", "One"],
          a: "Three",
          tip: "Count: time, reason, result!",
          successTip: "Three! Wake + work + breakfast! 🎯"
        },
        {
          type: "mc",
          q: "'Sarah usually exercises three times a week in the mornings.' This describes:",
          options: ["Frequency and time", "Only frequency", "Only time"],
          a: "Frequency and time",
          tip: "Both elements!",
          successTip: "Both! Complete description! 💪"
        },
        {
          type: "match",
          q: "Match complete routine elements.",
          pairs: [
            { left: "Frequency", right: "I always wake up early" },
            { left: "Time", right: "I exercise in the mornings" },
            { left: "Connector", right: "I'm tired, so I rest" }
          ],
          a: "completed",
          tip: "All elements combined!",
          successTip: "Complete descriptions! All elements! 🎯"
        },
        {
          type: "mc",
          q: "'I eat at home because it's healthier, but I sometimes eat out.' Best summary:",
          options: ["Prefers home, but flexible", "Only eats at home", "Always eats out"],
          a: "Prefers home, but flexible",
          tip: "Reason + exception!",
          successTip: "Preference with flexibility! 🍽️"
        },
        {
          type: "mc",
          q: "I usually ____ at 7, have ____, and go to ____:",
          options: ["wake up / breakfast /work.", "get up / lunch / swim", "sleep / dinner / car"],
          a: "I usually wake up at 7, have breakfast, and go to work.",
          tip: "Complete = detailed!",
          successTip: "Complete! Frequency + actions + sequence! 🌅"
        },
        {
          type: "order",
          q: "Traduce: 'Siempre me levanto a las 7 porque trabajo a las 8.'",
          words: ["I", "always", "wake", "up", "at", "7", "because", "I", "work", "at", "8."],
          a: "I always wake up at 7 because I work at 8.",
          tip: "Frequency + reason!"
        },
        {
          type: "input",
          q: "Complete: 'I usually ___ three times a week because it's healthy.'",
          a: "exercise",
          alts: ["do exercise", "exercises", "exercising", "workout","work out"],
          tip: "Frequency + reason!",
          successTip: "Frequency + reason! Complete thought! 💪"
        },
        {
          type: "order",
          q: "Traduce: 'Como en casa porque es más saludable, pero a veces como fuera.'",
          words: ["I", "eat", "at", "home", "because", "it's", "healthier,", "but", "I", "sometimes", "eat", "out."],
          a: "I eat at home because it's healthier, but I sometimes eat out.",
          extra: ["so", "and", "never"],
          tip: "Reason + contrast!"
        },
        {
          type: "mc",
          q: "Answer both 'How often and when?': 'I exercise ___'",
          options: ["three times a week in the mornings", "at the gym", "with friends"],
          a: "three times a week in the mornings",
          tip: "Both frequency and time!",
          successTip: "Both answers! Complete response! 🏋️"
        },
        {
          type: "input",
          q: "Final! YOUR complete routine: 'I usually exercise in the morning because it's ___.'",
          a: "healthy",
          alts: ["healthier","good","great","better","fit"],
          tip: "Your personal routine!",
          successTip: "✓ AMAZING!\nYou can describe complete routines! Unit 7 mastered! 🎉"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: David's Complete Week (FUERA DEL ARRAY)
  // ===================================================================
  readingSet: {
    id: "m3-u7-reading",
    title: "Reading: David's Weekly Routine 📅",
    category: "reading",

    questions: [
      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 1: Weekday Morning (preguntas 1-2)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Weekday Mornings**

My name is David and I work in an office in Ottawa. I always wake up at 7 AM on weekdays because I start work at 8:30. I usually have breakfast at home, but I sometimes skip it when I'm late.`
      },
      {
        type: "mc",
        q: "When does David wake up on weekdays?",
        options: ["At 7 AM", "At 8 AM", "At 6 AM"],
        a: "At 7 AM",
        tip: "Check the time!",
        successTip: "✓ 7 AM!\nRegular wake-up time! ⏰"
      },
      {
        type: "input",
        q: "How often does David have breakfast?",
        a: "usually",
        alts: ["usually has", "he usually"],
        tip: "Frequency adverb!",
        successTip: "✓ USUALLY!\nMost days he eats! 🍳"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 2: Work Routine (preguntas 3-5)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**At Work**

I go to work by bus every day. The bus ride takes 30 minutes, so I often read or listen to music. I never drive to work because parking is expensive. I usually arrive at the office around 8:15.`
      },
      {
        type: "mc",
        q: "How does David go to work?",
        options: ["By bus", "By car", "On foot"],
        a: "By bus",
        tip: "Transportation method!",
        successTip: "✓ BY BUS!\nPublic transportation! 🚌"
      },
      {
        type: "input",
        q: "How often does David drive to work?",
        a: "never",
        alts: [],
        tip: "Frequency adverb for 0%!",
        successTip: "✓ NEVER!\nNo car! 🚗❌"
      },
      {
        type: "mc",
        q: "Why doesn't David drive?",
        options: ["Because parking is expensive", "Because he doesn't have a car", "Because it's slow"],
        a: "Because parking is expensive",
        tip: "The reason given!",
        successTip: "✓ REASON!\nBecause = reason! 💰"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 3: Lunch & Afternoon (preguntas 6-7)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Lunch Time**

I always have lunch at 12:30. I usually bring lunch from home because it's cheaper, but I sometimes go to a restaurant with coworkers. After lunch, I often feel tired, so I have coffee.`
      },
      {
        type: "mc",
        q: "Where does David usually have lunch from?",
        options: ["From home", "From a restaurant", "From a café"],
        a: "From home",
        tip: "Usually = most often!",
        successTip: "✓ FROM HOME!\nBrings his own lunch! 🍱"
      },
      {
        type: "input",
        q: "What does David have after lunch?",
        a: "coffee",
        alts: [],
        tip: "Because he feels tired!",
        successTip: "✓ COFFEE!\nEnergy boost! ☕"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 4: After Work (preguntas 8-9)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Evenings**

I leave work at 5 PM. I go to the gym three times a week on Mondays, Wednesdays, and Fridays. On other days, I go straight home. I usually cook dinner, but I sometimes order food when I'm too tired.`
      },
      {
        type: "mc",
        q: "How often does David go to the gym?",
        options: ["Three times a week", "Every day", "Twice a week"],
        a: "Three times a week",
        tip: "Specific frequency!",
        successTip: "✓ THREE TIMES!\nRegular exercise! 🏋️"
      },
      {
        type: "input",
        q: "When does David go to the gym?",
        a: "Mondays Wednesdays Fridays",
        alts: ["Mon Wed Fri", "Monday Wednesday Friday", "on Mondays Wednesdays and Fridays"],
        tip: "Three specific days!",
        successTip: "✓ SPECIFIC DAYS!\nWhen = specific days! 📅"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 5: Weekend (pregunta 10)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Weekends**

On weekends, I never wake up early because I don't work. I usually wake up at 9 or 10 AM. I often meet friends for brunch on Saturdays. On Sundays, I always relax at home because I need rest for the next week.`
      },
      {
        type: "mc",
        q: "What does David always do on Sundays?",
        options: ["Relaxes at home", "Goes to the gym", "Meets friends"],
        a: "Relaxes at home",
        tip: "Always = 100%!",
        successTip: "✓ COMPLETE!\nYou understand David's complete routine! 🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (FUERA DEL ARRAY)
  // ===================================================================
  reviewSet: {
    id: "m3-u7-review",
    title: "Unit 7 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: Sarah goes to the gym Monday-Saturday. She ___ exercises.",
        options: ["usually", "rarely", "never"],
        a: "usually",
        tip: "6 out of 7 days!"
      },
      {
        type: "input",
        q: "Review: Tom is vegetarian, so he ___ eats meat.",
        a: "never",
        alts: [],
        tip: "Vegetarian = 0% meat!"
      },
      {
        type: "mc",
        q: "Review: 'I go to the gym ___ a week.' (3 days)",
        options: ["three times", "three time", "third times"],
        a: "three times",
        tip: "Number + times!"
      },
      {
        type: "input",
        q: "Review: Which asks frequency? '___ ___ do you exercise?'",
        a: "How often",
        alts: [],
        tip: "Frequency question!"
      },
      {
        type: "mc",
        q: "Review: 'I run ___ it's healthy.'",
        options: ["because", "so", "but"],
        a: "because",
        tip: "Reason = because!"
      },
      {
        type: "input",
        q: "Review: 'I'm tired, ___ I rest.'",
        a: "so",
        alts: [],
        tip: "Result = so!"
      },
      {
        type: "mc",
        q: "Review: Answer 'When do you study?'",
        options: ["In the evenings", "Three times a week", "For two hours"],
        a: "In the evenings",
        tip: "When = time!"
      },
      {
        type: "input",
        q: "Review: 'She works from home, so she ___ commutes.'",
        a: "rarely",
        alts: ["never"],
        tip: "Home = no travel!"
      },
      {
        type: "mc",
        q: "Review: He starts work at 8, so he wakes up:",
        options: ["early", "late", "at noon"],
        a: "early",
        tip: "Early work = early wake!"
      },
      {
        type: "mc",
        q: "Review: Best complete routine:",
        options: ["I usually wake up at 7, have breakfast, and go to work.", "I wake up.", "Morning."],
        a: "I usually wake up at 7, have breakfast, and go to work.",
        tip: "Complete description!",
        successTip: "🏆 UNIT 7 COMPLETE! Frequency, routines, and connectors mastered! Ready for Unit 8! 🚀"
      }
    ]
  }
};
